import React, { useEffect, useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Alert,
  CircularProgress,
  Container 
} from '@mui/material';
import { motion } from 'framer-motion';
import { ErrorOutline, BusinessOutlined } from '@mui/icons-material';

const DomainGuard = ({ children }) => {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [isValidDomain, setIsValidDomain] = useState(false);
  const [isCheckingDomain, setIsCheckingDomain] = useState(true);

  const allowedDomain = process.env.REACT_APP_ALLOWED_DOMAIN || 'autobia.sa';

  useEffect(() => {
    if (isLoaded && user) {
      setIsCheckingDomain(true);
      
      // Get primary email address
      const primaryEmail = user.primaryEmailAddress?.emailAddress;
      
      if (primaryEmail) {
        const emailDomain = primaryEmail.split('@')[1];
        const isValid = emailDomain === allowedDomain;
        
        setIsValidDomain(isValid);
        setIsCheckingDomain(false);
        
        // Log domain check for debugging (remove in production)
        console.log(`Domain check: ${primaryEmail} -> ${emailDomain} -> ${isValid ? 'ALLOWED' : 'DENIED'}`);
      } else {
        setIsValidDomain(false);
        setIsCheckingDomain(false);
      }
    }
  }, [isLoaded, user, allowedDomain]);

  // Show loading state while checking domain
  if (!isLoaded || isCheckingDomain) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FA9940 0%, #4A60F4 100%)',
        }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card elevation={24} sx={{ p: 4, textAlign: 'center', minWidth: 300 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1">Verifying access permissions...</Typography>
          </Card>
        </motion.div>
      </Box>
    );
  }

  // Show access denied if domain is not allowed
  if (!isValidDomain) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Card
              elevation={24}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                {/* Error Icon */}
                <Box sx={{ mb: 3 }}>
                  <ErrorOutline 
                    sx={{ 
                      fontSize: 80, 
                      color: 'error.main',
                      mb: 2 
                    }} 
                  />
                </Box>

                {/* Title */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: 'error.main',
                    mb: 2,
                  }}
                >
                  Access Restricted
                </Typography>

                {/* Message */}
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                    mb: 3,
                    lineHeight: 1.6,
                  }}
                >
                  This application is restricted to Autobia employees only.
                  <br />
                  You must use an <strong>@{allowedDomain}</strong> email address to access the design system.
                </Typography>

                {/* Current user info */}
                {user?.primaryEmailAddress && (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 3, textAlign: 'left' }}
                    icon={<BusinessOutlined />}
                  >
                    <Typography variant="body2">
                      <strong>Current account:</strong><br />
                      {user.primaryEmailAddress.emailAddress}
                    </Typography>
                  </Alert>
                )}

                {/* Instructions */}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                  }}
                >
                  Please sign out and sign in with your @autobia.sa company account,
                  or contact your administrator for access.
                </Typography>

                {/* Sign Out Button */}
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={() => signOut()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Sign Out & Try Again
                </Button>

                {/* Footer */}
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" color="text.secondary">
                    Autobia Design System - Employee Access Only
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Render protected content for valid domain users
  return <>{children}</>;
};

export default DomainGuard;